import type { SortDescriptor } from "@tql/ui-components-react/kendo/data-query";
import type { GridPageChangeEvent } from "@tql/ui-components-react/kendo/grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useItemsPerPagePreference } from "~/features/profile/hooks/use-items-per-page-preference.ts";
import type { QuoteBoardItem } from "~/shared/schemas/quote-board-schema.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import type { GridCompositeFilterDescriptor } from "../../utils/quote-board-grid-filter-utils.ts";

export type GroupExpandDescriptor = {
	value: unknown;
	field: string;
	expanded?: boolean;
	groups?: GroupExpandDescriptor[];
};

export type GridState = {
	sort: SortDescriptor[];
	skip: number;
	take: number;
	groupExpand: GroupExpandDescriptor[];
	filteredColumns: Set<string>;
	controlledFilter: GridCompositeFilterDescriptor | undefined;
	dataRefreshKey: number;
};

export type GridStateActions = {
	setSort: (sort: SortDescriptor[]) => void;
	setSkip: (skip: number) => void;
	setTake: (take: number) => void;
	setGroupExpand: (groupExpand: GroupExpandDescriptor[]) => void;
	setFilteredColumns: (
		columns: Set<string> | ((prev: Set<string>) => Set<string>),
	) => void;
	setControlledFilter: (
		filter: GridCompositeFilterDescriptor | undefined,
	) => void;
	incrementDataRefreshKey: () => void;
	resetGridState: () => void;
};

export const useQuoteBoardGridState = (resetKey: number) => {
	const {
		itemsPerPage,
		updateItemsPerPage,
		isLoading: isLoadingPreference,
	} = useItemsPerPagePreference(GridIdentifiers["Quote Board"]);

	// Core grid state
	const [sort, setSort] = useState<SortDescriptor[]>([
		{ field: "quoteId", dir: "desc" },
	]);
	const [skip, setSkip] = useState<number>(0);
	const [take, setTake] = useState<number>(itemsPerPage);
	const [groupExpand, setGroupExpand] = useState<GroupExpandDescriptor[]>([]);
	const [filteredColumns, setFilteredColumns] = useState<Set<string>>(
		new Set(),
	);
	const [controlledFilter, setControlledFilter] = useState<
		GridCompositeFilterDescriptor | undefined
	>(undefined);
	const [dataRefreshKey, setDataRefreshKey] = useState(0);

	// Once the user manually changes the page size, the local `take` becomes the
	// source of truth. Without this guard, the async preference round-trip (or a
	// stale PUT response) would re-emit the previous itemsPerPage and overwrite
	// the user's selection.
	const hasUserSetTake = useRef(false);

	// Sync take with itemsPerPage when the preference first loads (before any
	// manual interaction).
	useEffect(() => {
		if (!hasUserSetTake.current) {
			setTake(itemsPerPage);
		}
	}, [itemsPerPage]);

	// Reset tracking
	const hasProcessedInitialReset = useRef(false);
	const lastResetKeyRef = useRef(resetKey);
	const shouldForceReset = resetKey !== lastResetKeyRef.current;

	const resetGridState = useCallback(() => {
		setControlledFilter(undefined);
		setFilteredColumns(new Set());
		setSkip(0);
		setTake(itemsPerPage);
		setGroupExpand([]);
		setDataRefreshKey((prev) => prev + 1);
	}, [itemsPerPage]);

	const incrementDataRefreshKey = useCallback(() => {
		setDataRefreshKey((prev) => prev + 1);
	}, []);

	// Handle sort changes
	const handleSortChange = useCallback((event: { sort: SortDescriptor[] }) => {
		setSort(event.sort);
	}, []);

	// Handle group expand changes
	const handleGroupExpandChange = useCallback(
		(event: { groupExpand: GroupExpandDescriptor[] }) => {
			setGroupExpand(event.groupExpand);
		},
		[],
	);

	// Handle page changes
	const handlePageChange = useCallback(
		(event: GridPageChangeEvent, gridData: QuoteBoardItem[]) => {
			// Prevent page changes while the preference is still loading to ensure we
			// don't overwrite the user's saved preference before it's been fetched.
			if (isLoadingPreference) return;

			setSkip(event.page.skip);
			const newTake = event.page.take;
			if (typeof newTake === "string" && newTake === "All") {
				const dataLength = Array.isArray(gridData) ? gridData.length : 0;
				setTake(dataLength);
			} else {
				// Convert string to number if needed (Kendo may pass string values)
				const numericTake =
					typeof newTake === "string" ? Number(newTake) : newTake;
				// Only persist when the page SIZE changed. Next/Previous navigation
				// keeps the same `take`, so writing here would schedule a redundant
				// full-preference PUT.
				if (numericTake === take) return;
				hasUserSetTake.current = true;
				setTake(numericTake);
				updateItemsPerPage(numericTake);
			}
		},
		[updateItemsPerPage, isLoadingPreference, take],
	);

	const state: GridState = useMemo(
		() => ({
			sort,
			skip,
			take,
			groupExpand,
			filteredColumns,
			controlledFilter,
			dataRefreshKey,
		}),
		[
			sort,
			skip,
			take,
			groupExpand,
			filteredColumns,
			controlledFilter,
			dataRefreshKey,
		],
	);

	const actions: GridStateActions = useMemo(
		() => ({
			setSort,
			setSkip,
			setTake,
			setGroupExpand,
			setFilteredColumns,
			setControlledFilter,
			incrementDataRefreshKey,
			resetGridState,
		}),
		[incrementDataRefreshKey, resetGridState],
	);

	return {
		state,
		actions,
		handlers: {
			handleSortChange,
			handleGroupExpandChange,
			handlePageChange,
		},
		resetInfo: {
			shouldForceReset,
			hasProcessedInitialReset,
			lastResetKeyRef,
		},
		// True while the persisted items-per-page preference is still loading. Feed
		// this into the grid's `loading` prop to avoid a flash of the default page
		// size before the saved preference resolves.
		isLoadingPreference,
	};
};
